/**
 * Item catalogue — the client's mirror of `native/physics_nif/src/items.rs`.
 *
 * Ids are wire format: they arrive in the snapshot's item block and in the
 * PowerUpCollected / ItemUsed events. Everything the UI, renderer and audio
 * need to describe an item lives here so the three never disagree about what
 * a rocket looks like.
 */

export const ITEM_NONE      = 0;
export const ITEM_ROCKET    = 1;
export const ITEM_SPRING    = 2;
export const ITEM_GLIDE     = 3;
export const ITEM_MISSILE   = 4;
export const ITEM_TRAP      = 5;
export const ITEM_SHOCKWAVE = 6;

export const EFFECT_NONE   = 0;
export const EFFECT_ROCKET = 1;
export const EFFECT_SPRING = 2;
export const EFFECT_GLIDE  = 3;

export const HIT_MISSILE   = 1;
export const HIT_TRAP      = 2;
export const HIT_SHOCKWAVE = 3;

// Projectile kinds, read off a projectile entity's `effect` byte
export const PROJECTILE_MISSILE = 1;
export const PROJECTILE_TRAP    = 2;

/**
 * @typedef {Object} ItemDef
 * @property {string} name    display name
 * @property {string} icon    emoji shown in the HUD slot
 * @property {number} color   base colour, shared by the icon glow and the VFX
 * @property {boolean} offensive  aimed at other players rather than yourself
 * @property {string} blurb   one-line "what does this do" for the pickup toast
 */

/** @type {Record<number, ItemDef>} */
export const ITEMS = {
  [ITEM_ROCKET]: {
    name: 'Rocket Dash',
    icon: '🚀',
    color: 0xf97316,
    offensive: false,
    blurb: 'Blast forward',
  },
  [ITEM_SPRING]: {
    name: 'Spring Step',
    icon: '🌀',
    color: 0x22d3ee,
    offensive: false,
    blurb: 'Sky-high jumps',
  },
  [ITEM_GLIDE]: {
    name: 'Feather Glide',
    icon: '🪶',
    color: 0xa3e635,
    offensive: false,
    blurb: 'Float over gaps',
  },
  [ITEM_MISSILE]: {
    name: 'Egg Missile',
    icon: '🥚',
    color: 0xfbbf24,
    offensive: true,
    blurb: 'Homing shot',
  },
  [ITEM_TRAP]: {
    name: 'Slime Trap',
    icon: '🟢',
    color: 0x4ade80,
    offensive: true,
    blurb: 'Drop it behind you',
  },
  [ITEM_SHOCKWAVE]: {
    name: 'Primal Roar',
    icon: '💥',
    color: 0xf43f5e,
    offensive: true,
    blurb: 'Shove everyone nearby',
  },
};

/** Every item id, in catalogue order — the roulette cycles through these. */
export const ITEM_IDS = [
  ITEM_ROCKET,
  ITEM_SPRING,
  ITEM_GLIDE,
  ITEM_MISSILE,
  ITEM_TRAP,
  ITEM_SHOCKWAVE,
];

// ── Tuning mirror ─────────────────────────────────────────────────────────
//
// These must match `native/physics_nif/src/items.rs` and the glide constants
// in `systems.rs`. Client-side prediction re-simulates the local dino, so a
// mismatch here shows up as rubber-banding the moment a buff is active — which
// is exactly how the old powerups managed to feel like nothing at all.
export const ROCKET_SPEED_MULT = 1.6;
export const SPRING_JUMP_MULT = 1.42;
export const GLIDE_FALL_MULT = 0.28;
export const GLIDE_TERMINAL = -3.6;
export const GLIDE_DAMP = 7.0;

const UNKNOWN = { name: 'Item', icon: '❓', color: 0x94a3b8, offensive: false, blurb: '' };

/** @returns {ItemDef} */
export function itemDef(id) {
  return ITEMS[id] || UNKNOWN;
}

/** CSS colour for an item, for HUD markup that can't take a THREE colour. */
export function itemCss(id) {
  return `#${itemDef(id).color.toString(16).padStart(6, '0')}`;
}
